import { Rocket } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CardContent, CardHeader } from "@/components/ui/card";
import type { LaunchCharacterSummary } from "@/lib/core/schemas/launch-hub";
import { cn } from "@/lib/utils";

// Card's own styling reproduced on a native <article> instead of the
// role="group" div the Card primitive renders — <article> already carries
// an implicit accessible "article" role for a self-contained summary, so no
// ARIA role override is needed (see biome's useSemanticElements rule).
const CARD_CLASSNAME =
  "rounded-lg border bg-card text-card-foreground shadow-sm";

type CharacterCardState =
  | "available"
  | "in-campaign"
  | "archived-campaign"
  | "unsupported";

/**
 * Assignment state wins over version support: a character already
 * committed to a campaign (live or archived) shows that fact, since launch
 * eligibility is moot once assigned. Version support only gates the
 * not-yet-assigned "Available" case (plan D6).
 */
function deriveState(character: LaunchCharacterSummary): CharacterCardState {
  if (character.status.kind === "assigned") {
    return character.status.campaignArchived
      ? "archived-campaign"
      : "in-campaign";
  }
  return character.launchSupport.kind === "supported"
    ? "available"
    : "unsupported";
}

function classAndLevelLabel(character: LaunchCharacterSummary): string {
  const { classes } = character.preview.sheet;
  if (classes.length === 0) {
    return `Level ${character.preview.sheet.totalLevel}`;
  }
  return classes.map((c) => `${c.className} ${c.level}`).join(" / ");
}

function speciesLabel(character: LaunchCharacterSummary): string | null {
  const entry = character.preview.choiceSummary.find(
    (c) => c.category === "species"
  );
  return entry?.selectedLabels[0] ?? null;
}

type CharacterCardProps = {
  character: LaunchCharacterSummary;
  canLaunch: boolean;
  launchDisabledReason: string | null;
  onLaunch: (characterId: string) => void;
};

export function CharacterCard({
  character,
  canLaunch,
  launchDisabledReason,
  onLaunch,
}: CharacterCardProps) {
  const cardState = deriveState(character);
  const species = speciesLabel(character);
  const createdLabel = new Date(character.createdAt).toLocaleDateString();

  return (
    <article
      aria-label={character.name}
      className={cn(CARD_CLASSNAME, "flex h-full flex-col")}
    >
      <CardHeader className="space-y-1.5 p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-base leading-tight">
            {character.name}
          </h3>
          <StateChip state={cardState} />
        </div>
        <p className="text-muted-foreground text-sm">
          {species ? `${species} · ` : ""}
          {classAndLevelLabel(character)}
        </p>
        <p className="text-muted-foreground text-xs">Created {createdLabel}</p>
      </CardHeader>
      <CardContent className="mt-auto flex flex-col gap-2 p-4 pt-0">
        {cardState === "available" && (
          <div className="flex flex-col gap-1">
            <Button
              disabled={!canLaunch}
              onClick={() => onLaunch(character.id)}
              size="sm"
              type="button"
            >
              <Rocket className="h-4 w-4" />
              Launch a campaign
            </Button>
            {!canLaunch && launchDisabledReason && (
              <p className="text-muted-foreground text-xs">
                {launchDisabledReason}
              </p>
            )}
          </div>
        )}
        {cardState === "in-campaign" &&
          character.status.kind === "assigned" && (
            <Link
              className="text-primary text-sm underline-offset-4 hover:underline"
              to={`/campaigns/${character.status.campaignId}`}
            >
              View campaign
            </Link>
          )}
        {cardState === "archived-campaign" &&
          character.status.kind === "assigned" && (
            <p className="text-muted-foreground text-sm">
              {character.status.campaignName}
            </p>
          )}
        {cardState === "unsupported" && (
          <p className="text-muted-foreground text-sm">
            This character was built with a version of the ruleset this app no
            longer supports launching. It's kept for reference only.
          </p>
        )}
      </CardContent>
    </article>
  );
}

function StateChip({ state }: { state: CharacterCardState }) {
  switch (state) {
    case "available":
      return <Badge variant="secondary">Available</Badge>;
    case "in-campaign":
      return <Badge>In campaign</Badge>;
    case "archived-campaign":
      return <Badge variant="outline">Archived campaign</Badge>;
    case "unsupported":
      return <Badge variant="destructive">Version unsupported</Badge>;
    default:
      return null;
  }
}
